import {
  isDefined,
  isNotDefined,
  type CompanyId,
  LOCKABLE_USER_FIELDS,
  type LockableUserField,
  type UserId,
  type UserProfileData,
  type UserUpdateUser,
} from '@terros/common'
import { usePreference } from '../preferences'
import { usePermissionCheck } from '../permissions'
import { useProfile } from './get'

export const LOCKED_FIELD_MESSAGE = 'Only people who can manage users can change this field.'

export type LockedFields<T> = {
  lockedFields: T[]
  isLoading: boolean
}

export function useLockedUserFields(userId: UserId | undefined): LockedFields<LockableUserField> {
  const { user: currentUser, error: profileError } = useProfile()
  const { data: canManage, error: permissionError } = usePermissionCheck({
    permissionType: 'user:manage',
    targetId: toManageTarget(userId, currentUser),
  })
  const { data: lockedFields, error: preferenceError } = usePreference('lockedUserFields')
  if (isNotDefined(userId)) return { lockedFields: [], isLoading: false }
  const hasError = isDefined(profileError) || isDefined(permissionError) || isDefined(preferenceError)
  if (hasError || isNotDefined(currentUser) || isNotDefined(canManage) || isNotDefined(lockedFields)) {
    return { lockedFields: [...LOCKABLE_USER_FIELDS], isLoading: true }
  }
  if (canManage) return { lockedFields: [], isLoading: false }
  return { lockedFields, isLoading: false }
}

function toManageTarget(
  userId: UserId | undefined,
  currentUser: UserProfileData | undefined
): UserId | CompanyId | undefined {
  if (isNotDefined(currentUser)) return userId
  if (currentUser.userId !== userId) return userId
  return currentUser.companyId
}

export function omitLockedUserFields(user: UserUpdateUser, lockedFields: LockableUserField[]): UserUpdateUser {
  return lockedFields.reduce((rest, field) => ({ ...rest, [field]: undefined }), user)
}
